import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    EffectRef,
    inject,
    OnInit,
    Signal,
    signal,
    ViewEncapsulation,
    WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    CeBreadcrumbComponent,
    CeButtonComponent,
    CeContextMenuImports,
    CeEmptyStateImports,
    CeIconComponent,
    CeListImports,
    CeNotificationDataTags,
    CeNotificationListComponent,
    CeNotificationListInterface,
    CeScrollService,
    CeSnackbarService,
    CeSnackbarState,
    CeTabGroupImports,
    CeTabGroupTab,
    NOTIFICATION_COUNT_REGEX,
} from '@kempower-nx-workspace/shared/kp-design-system';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import {
    EnrichedFrontendNotification,
    fetchedNotifications,
    GetNotificationsWithSiteNamesResponse,
    GetSingleNotificationResponse,
    NotificationDataService,
    notificationStatuses,
} from '../../data-access/notification-data/notification-data.service';
import { Title } from '@angular/platform-browser';
import {
    CeTemplateHeaderScrollComponent,
    ContentSplitViewLayoutService,
    SMALL_SCREEN_WIDTH,
} from '@kempower-nx-workspace/layout-templates';
import { asapScheduler, filter, map, retry, timer } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
    ArchiveNotificationsResponse,
    ArchiveStatus,
    ExternalNotificationsApiServiceObservable,
    MarkReadNotificationsResponse,
    NotificationCounts,
} from '@kempoweroyj/ngx-external-notifications-api-client';
import { formatDate, formatTime } from '@kempower-nx-workspace/global-toolkit';
import { capitalize as _capitalize, last as _last } from 'lodash-es';
import { LocationsApiService, LocationsApiServiceObservable } from '@kempoweroyj/ngx-locations-api-client';
import { CeDocumentResizeObserverDirective, DocumentResizeEventType } from '@kempower-nx-workspace/shared-directives';
import { chargEyeTitle } from '@kempower-nx-workspace/chargeye-shared';
import {
    NotificationMessage,
    NotificationsManagementService,
    UserNotification,
} from '@kempower-nx-workspace/chargeye-notifications-management';

import { UnreadNotificationIndicatorService } from '../../data-access/notification-data/unread-notification-indicator.service';
import { SidePanelNavigationService } from '@kempower-nx-workspace/ce-side-navigation';

type EmptyStateDataType = {
    heading: string;
    description: string;
    actionLabel: string;
    actionHref: string;
};
const DELAY_MARK_AS_READ = 2000;
const DELAY_RETRY = 1000;

@Component({
    selector: 'ce-app-notifications-notifications-menu',
    imports: [
        CommonModule,
        CeButtonComponent,
        CeEmptyStateImports,
        CeNotificationListComponent,
        CeTabGroupImports,
        CeTemplateHeaderScrollComponent,
        CeBreadcrumbComponent,
        CeDocumentResizeObserverDirective,
        CeIconComponent,
        CeContextMenuImports,
        CeListImports,
        RouterLink,
    ],
    providers: [
        NotificationDataService,
        ExternalNotificationsApiServiceObservable,
        LocationsApiService,
        LocationsApiServiceObservable,
        CeScrollService,
    ],
    templateUrl: './notifications-menu.component.html',
    styleUrl: './notifications-menu.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationsMenuComponent implements OnInit {
    readonly #activatedRoute = inject(ActivatedRoute);
    readonly #titleService = inject(Title);
    readonly #router = inject(Router);
    readonly #notificationDataService = inject(NotificationDataService);
    readonly #splitViewLayoutService = inject(ContentSplitViewLayoutService);
    readonly #destroyRef = inject(DestroyRef);
    readonly #scrollService = inject(CeScrollService);
    readonly #notificationsManagement = inject(NotificationsManagementService);
    readonly #unreadNotificationIndicatorService = inject(UnreadNotificationIndicatorService);
    readonly #snackbarService = inject(CeSnackbarService);
    readonly #sidePanelNavigationService = inject(SidePanelNavigationService);

    notificationStatus = signal<ArchiveStatus>('inbox');
    selectedNotificationId = signal('');
    pageUrl = signal('');
    notificationsReady = signal(false);
    notifications: WritableSignal<Array<CeNotificationListInterface>> = signal([]);
    readStatusSignal: Signal<MarkReadNotificationsResponse | undefined> = toSignal(
        this.#notificationDataService.readStatusSubject$()
    );
    archivedStatusSignal: Signal<ArchiveNotificationsResponse | undefined> = toSignal(
        this.#notificationDataService.archiveStatusSubject$()
    );
    unreadCountsFeed: Signal<NotificationCounts | undefined> = toSignal(
        this.#notificationDataService.getUnreadCounts$()
    );
    unreadCounts: WritableSignal<NotificationCounts | undefined> = signal(this.unreadCountsFeed());
    notificationMessagesSignal: Signal<NotificationMessage | undefined> = toSignal(
        this.#notificationsManagement.notifications$()
    );
    newNotificationLoading = signal(false);
    tabIndexFromRoute = signal(0);

    readonly archivedTag = { default: 'Archived' };
    readonly scrollId = 'notifications-menu';
    tabs: Array<CeTabGroupTab> = [];
    getMoreNotificationsTriggered = false;
    wasLastPage = false;
    infiniteScrollContainer = `#${this.scrollId}`;
    allMarkedAsRead: boolean | undefined = undefined;
    timerMarkSingleAsRead: ReturnType<typeof setTimeout> | undefined = undefined;
    navigatedToNotificationWithUrl = true;
    navigatedFromToast = false;

    hasLoaded: Signal<boolean> = computed(() => {
        return this.notificationsReady() && this.unreadCounts()?.all !== undefined;
    });

    emptyStateData: Signal<EmptyStateDataType> = computed((): EmptyStateDataType => {
        let heading = '';
        let description = '';
        let actionLabel = '';
        let actionHref = '';
        const unreadCountArchived =
            (this.unreadCounts()?.archived?.sent as number) + (this.unreadCounts()?.archived?.read as number);
        const unreadCountInbox =
            (this.unreadCounts()?.inbox?.sent as number) + (this.unreadCounts()?.inbox?.read as number);

        switch (this.notificationStatus()) {
            case 'inbox':
                heading = 'Inbox zero';
                description = 'Your notifications inbox is empty.';

                if (unreadCountArchived > 0) {
                    heading = 'Inbox';
                    description =
                        'Your notifications inbox is empty. However, there is something in your notifications archive.';
                    actionLabel = 'Notifications archived';
                    actionHref = 'notifications/archived';
                }
                break;

            case 'archived':
                heading = 'No archived notifications';
                description = 'Your notifications archive is empty.';

                if (unreadCountInbox > 0) {
                    description =
                        'Your notifications archive is empty. However, there is something in your notifications inbox.';
                    actionLabel = 'Notifications inbox';
                    actionHref = 'notifications/inbox';
                }
                break;

            case 'all':
                heading = 'No notifications yet';
                description =
                    'You haven’t received any notifications. To subscribe to some notifications about events in your charging network, go to Notification settings.';
                actionLabel = 'Notification settings';
                actionHref = 'profile-settings/notification-settings';
                break;
        }

        return {
            heading: heading,
            description: description,
            actionLabel: actionLabel,
            actionHref: actionHref,
        };
    });

    ngOnInit(): void {
        this.#notificationDataService.clearFetchedNotifications();

        this.#splitViewLayoutService.setSecondViewVisibility(false);

        this.notificationStatus.set(this.#activatedRoute.snapshot.paramMap.get('notificationStatus') as ArchiveStatus);
        const currentNotificationAmount =
            RegExp(NOTIFICATION_COUNT_REGEX).exec(this.#titleService.getTitle())?.[0] ?? '';
        this.#titleService.setTitle(currentNotificationAmount + _capitalize(this.notificationStatus()) + chargEyeTitle);
        this.updateNotificationsList();

        notificationStatuses.forEach((status: ArchiveStatus) => {
            this.tabs.push({
                label: String(status).charAt(0).toUpperCase() + String(status).slice(1),
                path: `/notifications/${status}`,
                count: 0, // Needed that the skeleton loader works
            });
        });
        this.tabIndexFromRoute.set(notificationStatuses.indexOf(this.notificationStatus()));

        const notificationIdFromUrl = _last(this.#router.url.split('/'));
        if (!notificationStatuses.includes(notificationIdFromUrl as ArchiveStatus))
            this.selectedNotificationId.set(notificationIdFromUrl as string);

        // If there's a "new notifications indicator" in SidePanel, remove it after a delay.
        if (this.notificationStatus() === 'inbox') {
            this.clearSidePanelIndicator();
        }
    }

    clearSidePanelIndicator(): void {
        if (this.#sidePanelNavigationService.hasNewNotification()) {
            setTimeout(() => {
                this.#sidePanelNavigationService.hasNewNotification.set(false);
                this.#unreadNotificationIndicatorService
                    .setUnreadIndicatorFalse$()
                    .pipe(takeUntilDestroyed(this.#destroyRef))
                    .subscribe();
            }, DELAY_MARK_AS_READ);
        }
    }

    onScreenResize($event: DocumentResizeEventType): void {
        if ($event.contentWidth <= SMALL_SCREEN_WIDTH) {
            this.infiniteScrollContainer = '';
        } else {
            this.infiniteScrollContainer = `#${this.scrollId}`;
        }
    }

    selectNotificationById(params: { id: string; unread: boolean }): void {
        this.selectedNotificationId.set(params.id);
        this.markNotificationReadAfterDelay(params.id, params.unread);
        this.#router.navigate([this.pageUrl() + '/' + params.id]);
        this.navigatedToNotificationWithUrl = false;
    }

    createListItem(notification: EnrichedFrontendNotification, unread: boolean): CeNotificationListInterface {
        const tags: Array<CeNotificationDataTags> = [];
        // Only add status tag if not scheduled-session-ended-unexpectedly or subscription-is-about-to-expire
        if (
            notification.eventType !== 'scheduled-session-ended-unexpectedly' &&
            notification.eventType !== 'subscription-is-about-to-expire'
        ) {
            const statusTag: CeNotificationDataTags =
                notification.eventState === 'active' ? { info: 'Active' } : { emphasis: 'Resolved' };
            tags.push(statusTag);
        }
        if (notification.archived) {
            tags.push(this.archivedTag);
        }

        // For subscriptions events, use organization name instead of site name
        const notificationLabel =
            notification.eventType === 'subscription-is-about-to-expire'
                ? notification.events?.[0]?.metadata?.['tenantName'] || notification.siteName
                : notification.siteName;

        return {
            title: notification.title,
            date:
                // Show time if timestamp is today, date otherwise
                new Date(notification.notificationTimestamp).setHours(0, 0, 0, 0) !== new Date().setHours(0, 0, 0, 0)
                    ? formatDate(notification.notificationTimestamp)
                    : formatTime(notification.notificationTimestamp),
            notificationId: notification.id,
            notificationLabel: notificationLabel,
            tags: tags,
            unread: unread,
        };
    }

    getNewNotificationData(notificationId: string): void {
        this.#notificationDataService
            .getSingleNotification$(notificationId)
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .pipe(
                map((res: GetSingleNotificationResponse) => {
                    if (res.status === 'success' && res.notification) {
                        // Add freshly arrived notification to notification list
                        const newNotification = this.createListItem(res.notification, true);
                        fetchedNotifications.set([res.notification].concat(fetchedNotifications()));
                        this.notifications.set([newNotification].concat(this.notifications()));
                        this.newNotificationLoading.set(false);
                    }
                }),
                retry({
                    count: 5,
                    delay: () => timer(DELAY_RETRY),
                })
            )
            .subscribe();
    }

    getNotifications(paginationToken?: boolean): void {
        this.#notificationDataService
            .getNotificationsWithSiteNames$(this.notificationStatus(), paginationToken)
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe((event: GetNotificationsWithSiteNamesResponse) => {
                if (!this.getMoreNotificationsTriggered) {
                    this.notifications.set([]);
                }

                const notificationsList: Array<CeNotificationListInterface> = [];
                this.wasLastPage = event.wasLastPage;

                event.data?.forEach((notification: EnrichedFrontendNotification) => {
                    // There's a latency marking notifications updating notifications' "read" status,
                    // so we need to override the states coming from 'getMoreNotifications'
                    const readStatus = this.allMarkedAsRead ?? notification.status === 'read';

                    notificationsList.push(this.createListItem(notification, !readStatus));

                    // Mark selected notification as read on page land
                    if (
                        this.selectedNotificationId() === notification.id &&
                        notification.status !== 'read' &&
                        !this.navigatedFromToast
                    ) {
                        this.markNotificationReadAfterDelay(notification.id, true);
                    }
                });
                this.notifications.set(this.notifications().concat(notificationsList));
                this.notificationsReady.set(event.status === 'success');

                // If navigation was initiated from toast notification, update the current notification selected
                if (this.navigatedFromToast && event.status === 'success') {
                    const currentNotificationId = _last(this.#router.url.split('/')) as string;
                    this.selectedNotificationId.set(currentNotificationId);
                    this.#splitViewLayoutService.setSecondViewVisibility(true);
                    this.markNotificationReadAfterDelay(currentNotificationId, this.notifications()[0].unread ?? true);
                    this.navigatedFromToast = false;
                }

                if (
                    event.status === 'success' &&
                    this.selectedNotificationId() &&
                    !this.getMoreNotificationsTriggered
                ) {
                    setTimeout(() => {
                        this.#scrollService.scrollToElement(this.selectedNotificationId(), 'notifications-menu', -100);
                    }, 100);
                }
            });
    }

    getMoreNotifications(): void {
        if (!this.wasLastPage && this.notificationsReady()) {
            this.getMoreNotificationsTriggered = true;
            this.getNotifications();
        }
    }

    updateNotificationsList(clearPaginationToken?: boolean): void {
        this.notificationStatus.set(this.#activatedRoute.snapshot.paramMap.get('notificationStatus') as ArchiveStatus);
        this.notificationsReady.set(false);
        this.pageUrl.set('/notifications/' + this.notificationStatus());

        this.getNotifications(clearPaginationToken);
    }

    updateReadStatusSingle(id: string): void {
        this.#notificationDataService.updateNotificationReadStatus({
            markAll: false,
            notificationIds: [id],
            read: true,
            archiveStatusFilter: this.notificationStatus(),
        });
    }

    updateReadStatusAll(markAsRead: boolean): void {
        this.#notificationDataService.updateNotificationReadStatus({
            markAll: true,
            notificationIds: [],
            read: markAsRead,
            archiveStatusFilter: this.notificationStatus(),
        });
    }

    markNotificationReadAfterDelay(id: string, unread: boolean): void {
        clearTimeout(this.timerMarkSingleAsRead);

        if (unread && id) {
            // Mark notification as read after delay if it's still the one selected
            this.timerMarkSingleAsRead = setTimeout(() => {
                if (this.selectedNotificationId() === id) {
                    this.updateReadStatusSingle(id);
                }
            }, DELAY_MARK_AS_READ);
        }
    }

    updateArchivedStatusAll(markAsArchived: boolean): void {
        this.#notificationDataService.updateNotificationArchivedStatus({
            markAll: true,
            notificationIds: [],
            archiveStatus: markAsArchived,
        });
    }

    updateUnreadCountsWhenChangingReadStatus(
        unread: boolean,
        notificationStatus: ArchiveStatus,
        single: boolean
    ): void {
        const sentCount = this.unreadCounts()?.[notificationStatus]?.sent as number;
        const readCount = this.unreadCounts()?.[notificationStatus]?.read as number;

        if (single) {
            this.unreadCounts.update(
                (existingData: NotificationCounts | undefined) =>
                    <NotificationCounts>{
                        ...existingData,
                        [notificationStatus]: {
                            read: unread ? readCount - 1 : readCount + 1,
                            sent: unread ? sentCount + 1 : sentCount - 1,
                        },
                    }
            );
        } else {
            this.unreadCounts.update(
                (existingData: NotificationCounts | undefined) =>
                    <NotificationCounts>{
                        ...existingData,
                        [notificationStatus]: {
                            sent: unread ? readCount + sentCount : 0,
                            read: unread ? 0 : readCount + sentCount,
                        },
                    }
            );
        }
    }

    updateAllTabUnreadCountWhenChangingReadStatus(): void {
        this.unreadCounts.update(
            (existingData: NotificationCounts | undefined) =>
                <NotificationCounts>{
                    ...existingData,
                    ['all']: {
                        sent:
                            (this.unreadCounts()?.['inbox']?.sent as number) +
                            (this.unreadCounts()?.['archived']?.sent as number),
                        read:
                            (this.unreadCounts()?.['inbox']?.read as number) +
                            (this.unreadCounts()?.['archived']?.read as number),
                    },
                }
        );
    }

    updateSingleNotificationReadStatus(): Array<CeNotificationListInterface> {
        const updatedNotifications: Array<CeNotificationListInterface> = [];
        const unread = this.readStatusSignal()?.readStatus !== 'read';

        this.notifications().forEach((notification) => {
            if (notification.notificationId === this.readStatusSignal()?.notificationIds[0]) {
                notification.unread = unread;

                // Update the current tab
                this.updateUnreadCountsWhenChangingReadStatus(unread, this.notificationStatus(), true);

                if (this.notificationStatus() !== 'all') {
                    // Update 'all' tab if 'inbox' or 'archived' tab is selected
                    this.updateAllTabUnreadCountWhenChangingReadStatus();
                } else if (notification.tags?.includes(this.archivedTag)) {
                    // Update 'archived' tab if 'all' tab is selected
                    this.updateUnreadCountsWhenChangingReadStatus(unread, 'archived', true);
                } else {
                    // Update 'inbox' tab if 'all' tab is selected
                    this.updateUnreadCountsWhenChangingReadStatus(unread, 'inbox', true);
                }
            }
            updatedNotifications.push(notification);
        });
        return updatedNotifications;
    }

    updateAllNotificationReadStatus(): Array<CeNotificationListInterface> {
        const updatedNotifications: Array<CeNotificationListInterface> = [];
        const unread = this.readStatusSignal()?.readStatus !== 'read';

        this.notifications().forEach((notification) => {
            notification.unread = unread;
            updatedNotifications.push(notification);
        });

        if (this.notificationStatus() === 'archived') {
            // Update 'archived' tab's counts, when it's selected
            this.updateUnreadCountsWhenChangingReadStatus(unread, 'archived', false);
        } else if (this.notificationStatus() === 'inbox') {
            // Update 'inbox' tab's counts, when it's selected
            this.updateUnreadCountsWhenChangingReadStatus(unread, 'inbox', false);
        } else if (this.notificationStatus() === 'all') {
            // Update 'all' tab's counts, when it's selected
            this.updateUnreadCountsWhenChangingReadStatus(unread, 'archived', false);
            this.updateUnreadCountsWhenChangingReadStatus(unread, 'inbox', false);
            this.updateUnreadCountsWhenChangingReadStatus(unread, 'all', false);
        }
        if (this.notificationStatus() !== 'all') {
            // Update 'all' tab's counts, when either 'inbox' or 'archived' tab is selected
            this.updateAllTabUnreadCountWhenChangingReadStatus();
        }

        return updatedNotifications;
    }

    updateUnreadCountsWhenArchiving(archived: boolean, single: boolean, unread?: boolean): void {
        const archivedSentCount = this.unreadCounts()?.['archived']?.sent as number;
        const archivedReadCount = this.unreadCounts()?.['archived']?.read as number;
        const inboxSentCount = this.unreadCounts()?.['inbox']?.sent as number;
        const inboxReadCount = this.unreadCounts()?.['inbox']?.read as number;
        const firstTabSent = archived ? archivedSentCount : inboxSentCount;
        const firstTabRead = archived ? archivedReadCount : inboxReadCount;
        const secondTabSent = archived ? inboxSentCount : archivedSentCount;
        const secondTabRead = archived ? inboxReadCount : archivedReadCount;
        const firstTab = archived ? 'archived' : 'inbox';
        const secondTab = archived ? 'inbox' : 'archived';

        // Update the unread counts of 'inbox' and 'archived' tabs, when single or all notifications are marked as archived
        if (single) {
            this.unreadCounts.update(
                (existingData: NotificationCounts | undefined) =>
                    <NotificationCounts>{
                        ...existingData,
                        [firstTab]: {
                            sent: unread ? firstTabSent - 1 : firstTabSent,
                            read: unread ? firstTabRead : firstTabRead - 1,
                        },
                        [secondTab]: {
                            sent: unread ? secondTabSent + 1 : secondTabSent,
                            read: unread ? secondTabRead : secondTabRead + 1,
                        },
                    }
            );
        } else {
            this.unreadCounts.update(
                (existingData: NotificationCounts | undefined) =>
                    <NotificationCounts>{
                        ...existingData,
                        [secondTab]: {
                            sent: 0,
                            read: 0,
                        },
                        [firstTab]: {
                            sent: firstTabSent + secondTabSent,
                            read: firstTabRead + secondTabRead,
                        },
                    }
            );
        }
    }

    updateSingleNotificationArchivedStatus(): Array<CeNotificationListInterface> {
        const updatedNotifications: Array<CeNotificationListInterface> = [];
        this.notifications().forEach((notification) => {
            if (notification.notificationId === this.archivedStatusSignal()?.notificationIds[0]) {
                const unread = notification.unread as boolean;
                const archived = notification.tags?.includes(this.archivedTag) as boolean;

                this.updateUnreadCountsWhenArchiving(archived, true, unread);

                if (this.archivedStatusSignal()?.archiveStatus) {
                    notification.tags?.push(this.archivedTag);
                } else {
                    notification.tags?.pop();
                }
                // Navigate to "root", so the selected notification seems to get unselected on its own
                this.selectedNotificationId.set('');
                this.#router.navigate(['/notifications/' + this.notificationStatus()]);
            } else {
                updatedNotifications.push(notification);
            }
        });
        return updatedNotifications;
    }

    updateAllNotificationArchivedStatus(): Array<CeNotificationListInterface> {
        this.updateUnreadCountsWhenArchiving(this.archivedStatusSignal()?.archiveStatus as boolean, false);

        this.notifications().forEach((notification) => {
            if (this.archivedStatusSignal()?.archiveStatus) {
                notification.tags?.push(this.archivedTag);
            } else {
                notification.tags?.pop();
            }
        });
        if (this.notificationStatus() === 'all') {
            return this.notifications();
        } else {
            return [];
        }
    }

    updateTabsUnreadCounts = effect(() => {
        if (this.unreadCountsFeed()) {
            this.tabs.forEach((tab: CeTabGroupTab) => {
                const label = tab.label.toLowerCase() as ArchiveStatus;
                let count: number | string = this.unreadCounts()?.[label]?.['sent'] ?? 0;
                if (count > 99) {
                    count = '99+';
                }
                tab.count = count;
            });
        }
    });

    updateUnreadCountsOnlyOnce: EffectRef = effect(
        () => {
            if (this.unreadCountsFeed()) {
                // Update unreadCounts() according to unreadCountsFeed() only once
                asapScheduler.schedule(() => {
                    this.unreadCounts.set(this.unreadCountsFeed());
                });
            }
        },
        { manualCleanup: true }
    );

    updateNotificationListOnReadStatusChange: EffectRef = effect(() => {
        let isSingle = true;
        let isRead = true;

        if (this.readStatusSignal() !== undefined) {
            isSingle = this.readStatusSignal()?.notificationIds.length === 1;
            isRead = this.readStatusSignal()?.readStatus === 'read';
        }

        if (this.readStatusSignal()?.success) {
            if (isSingle) {
                // "Single"
                clearTimeout(this.timerMarkSingleAsRead);
                asapScheduler.schedule(() => {
                    this.notifications.set(this.updateSingleNotificationReadStatus());
                });
            } else {
                // "All"
                asapScheduler.schedule(() => {
                    this.notifications.set(this.updateAllNotificationReadStatus());
                });
                this.allMarkedAsRead = isRead;
            }
        } else if (this.readStatusSignal()?.success === false) {
            asapScheduler.schedule(() => {
                this.#snackbarService.showSnackbar({
                    message: `Something went wrong while marking ${isSingle ? 'this notification' : 'these notifications'} as ${isRead ? 'read' : 'unread'}. Please try again shortly.`,
                    duration: 8000,
                    state: CeSnackbarState.ERROR,
                });
            });
        }
    });

    updateNotificationListOnArchivedStatusChange: EffectRef = effect(() => {
        let isSingle = true;

        if (this.archivedStatusSignal() !== undefined) {
            isSingle = this.archivedStatusSignal()?.notificationIds.length === 1;
        }

        if (this.archivedStatusSignal()?.success) {
            if (isSingle) {
                // "Single"
                asapScheduler.schedule(() => {
                    this.notifications.set(this.updateSingleNotificationArchivedStatus());
                });
            } else {
                // "All"
                asapScheduler.schedule(() => {
                    this.notifications.set(this.updateAllNotificationArchivedStatus());
                });
            }
        } else if (this.archivedStatusSignal()?.success === false) {
            const isArchived = this.archivedStatusSignal()?.archiveStatus;
            asapScheduler.schedule(() => {
                this.#snackbarService.showSnackbar({
                    message: `Something went wrong while ${isArchived ? 'archiving' : 'unarchiving'} ${isSingle ? 'this notification' : 'these notifications'}. Please try again shortly.`,
                    duration: 8000,
                    state: CeSnackbarState.ERROR,
                });
            });
        }
    });

    handleRouteChanges = effect(() => {
        this.#router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe((event) => {
                const isNavigatingToSingleNotification =
                    event.url.includes(this.pageUrl()) && event.url !== this.pageUrl();
                this.#splitViewLayoutService.setSecondViewVisibility(isNavigatingToSingleNotification);
                window.scrollTo(0, 0);
                const currentNotificationId = _last(event.url.split('/')) as string;

                // When user clicks link inside toast notification
                if (
                    this.navigatedToNotificationWithUrl &&
                    !notificationStatuses.includes(currentNotificationId as ArchiveStatus) &&
                    currentNotificationId !== 'notifications'
                ) {
                    // Select 'inbox' tab, when user navigates to the new notification
                    if (!event.url.includes(this.pageUrl())) {
                        // The current tab is some other than 'inbox'
                        this.#splitViewLayoutService.setSecondViewVisibility(false);
                        this.selectedNotificationId.set('');
                        this.navigatedFromToast = true;

                        this.updateNotificationsList(true);
                        this.tabIndexFromRoute.set(notificationStatuses.indexOf(this.notificationStatus()));

                        this.wasLastPage = false;
                        this.getMoreNotificationsTriggered = false;
                    } else {
                        // The current tab is already 'inbox'
                        this.#splitViewLayoutService.setSecondViewVisibility(false);
                        this.selectedNotificationId.set(currentNotificationId);
                        this.#splitViewLayoutService.setSecondViewVisibility(true);
                        this.markNotificationReadAfterDelay(
                            this.selectedNotificationId(),
                            this.notifications()[0].unread ?? true
                        );

                        setTimeout(() => {
                            this.#scrollService.scrollToElement(
                                this.selectedNotificationId(),
                                'notifications-menu',
                                -100
                            );
                        }, 100);
                    }
                }

                // Update list if the current tab changes
                else if (!event.url.includes(this.notificationStatus())) {
                    this.notifications.set([]);
                    this.wasLastPage = false;
                    this.getMoreNotificationsTriggered = false;
                    this.updateNotificationsList(true);
                    this.tabIndexFromRoute.set(notificationStatuses.indexOf(this.notificationStatus()));

                    // Clear current notification selected, so switching back and forth tabs don't open it up
                    this.selectedNotificationId.set('');
                }

                // If there's a "new notifications indicator" in SidePanel, remove it after a delay.
                if (event.url.includes('inbox')) {
                    this.clearSidePanelIndicator();
                }
                this.#titleService.setTitle(_capitalize(this.notificationStatus()) + chargEyeTitle);
                this.navigatedToNotificationWithUrl = true;
            });
    });

    // Update 'inbox' list when a toast telling about a new notification is shown
    updateInboxListWhenNewArrives = effect(() => {
        if (this.notificationMessagesSignal()?.type === 'UserNotification') {
            asapScheduler.schedule(() => {
                if (this.tabIndexFromRoute() === 0) {
                    this.newNotificationLoading.set(true);
                    this.getNewNotificationData((this.notificationMessagesSignal() as UserNotification).notificationId as string);
                }
                this.updateUnreadCountsWhenChangingReadStatus(true, 'inbox', true);
                this.updateUnreadCountsWhenChangingReadStatus(true, 'all', true);
            });
        }
    });
}
